import React, { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Card, Button, Input, Table, Spin, message, Typography, Tag, Space,
  Modal, Form, Select, Popconfirm, Upload, Image, DatePicker,
} from 'antd';
import {
  SendOutlined, RobotOutlined, StarOutlined, ReloadOutlined,
  SettingOutlined, ThunderboltOutlined, DeleteOutlined, PictureOutlined,
} from '@ant-design/icons';
import { aiApi, strategyApi } from '../services/api';

const { Title, Text } = Typography;

// 容错解析初选接口返回（支持多种结构/字段别名）
function parseStocks(resp) {
  if (!resp) return [];
  let arr = null;
  if (Array.isArray(resp)) arr = resp;
  else if (Array.isArray(resp.data)) arr = resp.data;
  else if (Array.isArray(resp.stocks)) arr = resp.stocks;
  else if (Array.isArray(resp.list)) arr = resp.list;
  else if (resp.raw) return parseStocks(JSON.parse(resp.raw));
  if (!arr) return [];
  return arr.map((s) => ({
    code: s.code || s.stock_code || s.symbol || s.stockCode || '',
    name: s.name || s.stock_name || s.stockName || '',
    price: Number(s.price || s.now_price || s.current_price || s.last_price || s.现价 || 0) || 0,
  })).filter((s) => s.code || s.name);
}

export default function AiPickPage() {
  // ----- 聊天 -----
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // 待发送图片 {url, base64}
  const chatEndRef = useRef(null);

  // 进入页面加载当前用户聊天历史（持久化）
  useEffect(() => {
    (async () => {
      try {
        const res = await aiApi.getChatHistory();
        if (res.success && Array.isArray(res.data.list) && res.data.list.length) {
          setMessages(res.data.list.map((m) => ({
            role: m.role,
            content: m.content || '',
            image: m.image_url || null,
          })));
        } else {
          setMessages([
            { role: 'assistant', content: '你好，我是股小T。告诉我你的选股偏好，比如行业、市值、技术形态，我帮你分析。' },
          ]);
        }
      } catch (e) {
        setMessages([
          { role: 'assistant', content: '你好，我是股小T。告诉我你的选股偏好，比如行业、市值、技术形态，我帮你分析。' },
        ]);
      }
    })();
  }, []);

  // ----- 初选 / 终选 -----
  const [preliminary, setPreliminary] = useState([]);
  const [preliminarySearch, setPreliminarySearch] = useState('');
  const [loadingPre, setLoadingPre] = useState(false);
  const [dateFilter, setDateFilter] = useState(dayjs()); // 初选股票日期筛选，默认今日
  const [finalList, setFinalList] = useState([]);

  // 初选股票搜索过滤（按代码/名称）
  const filteredPre = useMemo(() => {
    const kw = preliminarySearch.trim().toLowerCase();
    if (!kw) return preliminary;
    return preliminary.filter(
      (s) => (s.code || '').toLowerCase().includes(kw) || (s.name || '').toLowerCase().includes(kw)
    );
  }, [preliminary, preliminarySearch]);

  // ----- 策略维护 -----
  const [strategies, setStrategies] = useState([]);

  // ----- 策略维护弹窗 -----
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [stratLoading, setStratLoading] = useState(false);
  const [stratFormOpen, setStratFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 进入页面默认加载今日的初选/终选股票（仅查库）
  useEffect(() => {
    loadPreliminaryByDate(dateFilter);
    loadTodayFinal();
  }, []);

  // ===== 聊天 =====
  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && !pendingImage) || chatLoading) return;
    const userMsg = {
      role: 'user',
      content: text,
      image: pendingImage ? pendingImage.base64 : null,
    };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput('');
    setPendingImage(null);
    setChatLoading(true);

    // 构造发给大模型的消息（图片转多模态 content）
    const toApiMsg = (m) => {
      if (m.image) {
        const parts = [];
        if (m.content) parts.push({ type: 'text', text: m.content });
        parts.push({ type: 'image_url', image_url: { url: m.image } });
        return { role: m.role, content: parts };
      }
      return { role: m.role, content: m.content };
    };

    try {
      const res = await aiApi.chat(next.map(toApiMsg));
      const reply = res.success ? (res.data.content || '') : ('⚠️ ' + (res.message || '调用失败'));
      const finalMsgs = [...next, { role: 'assistant', content: reply }];
      setMessages(finalMsgs);
      // 持久化：保存 user 与 assistant 两条
      await aiApi.saveChat({ role: 'user', content: text, image_url: userMsg.image || '' });
      await aiApi.saveChat({ role: 'assistant', content: reply });
    } catch (e) {
      const finalMsgs = [...next, { role: 'assistant', content: '⚠️ 网络错误：' + (e.message || '') }];
      setMessages(finalMsgs);
      try {
        await aiApi.saveChat({ role: 'user', content: text, image_url: userMsg.image || '' });
        await aiApi.saveChat({ role: 'assistant', content: finalMsgs[finalMsgs.length - 1].content });
      } catch (_) {}
    } finally {
      setChatLoading(false);
    }
  };

  // 清空聊天（软删除，仅当前用户）
  const clearChat = async () => {
    try {
      const res = await aiApi.clearChat();
      if (res.success) {
        setMessages([]);
        message.success('聊天记录已清空');
      } else {
        message.error(res.message || '清空失败');
      }
    } catch (e) {
      message.error('清空失败：' + (e.message || ''));
    }
  };

  // 选择图片（转为 base64 暂存，发送时一并发给 AI）
  const beforeImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = () => setPendingImage({ url: reader.result, base64: reader.result });
    reader.readAsDataURL(file);
    return false; // 阻止自动上传
  };

  // ===== 初选 / 终选 =====
  // 按所选日期加载初选股票（仅查库，不抓远程）；默认显示 dateFilter（今日）
  const loadPreliminaryByDate = async (date) => {
    setLoadingPre(true);
    try {
      const d = dayjs(date).format('YYYY-MM-DD');
      const res = await aiApi.getPreliminary({ date: d });
      if (res.success) {
        const raw = res.data || {};
        const list = Array.isArray(raw.list) ? raw.list : parseStocks(raw);
        setPreliminary(list);
      } else {
        message.error(res.message || '加载初选失败');
      }
    } catch (e) {
      // 进入页面静默失败，不影响使用
    } finally {
      setLoadingPre(false);
    }
  };

  // 抓取远程初选接口并入库（远程返回的是今日数据），成功后刷新为当前筛选日期（默认今日）
  const fetchPreliminary = async () => {
    setLoadingPre(true);
    try {
      const res = await aiApi.getPreliminary();
      if (res.success) {
        const raw = res.data || {};
        if (raw.store_error) {
          message.error('入库失败: ' + raw.store_error);
        } else {
          const fetched = (Array.isArray(raw.list) ? raw.list : parseStocks(raw)).length;
          message.success(`已获取初选股票 ${fetched} 只（本次入库 ${raw.stored_count ?? 0} 条）`);
        }
        // 抓取后只刷新显示当前筛选日期（默认今日），避免一次性显示所有历史
        loadPreliminaryByDate(dateFilter);
      } else {
        message.error(res.message || '获取初选失败');
      }
    } catch (e) {
      message.error('获取初选失败：' + (e.message || ''));
    } finally {
      setLoadingPre(false);
    }
  };

  const addToFinal = (row) => {
    if (finalList.some((s) => s.code === row.code)) {
      message.info('已在终选列表中');
      return;
    }
    const next = [...finalList, { ...row, rank: finalList.length + 1 }];
    setFinalList(next);
    saveFinalList(next, 'manual');
    message.success(`已加入终选：${row.name || row.code}`);
  };
  const removeFinal = (code) => {
    const next = finalList.filter((s) => s.code !== code);
    setFinalList(next);
    saveFinalList(next, 'manual');
  };

  // 单只初选股票发送 AI 打分（带 K 线），结果写入终选并落库
  const scoreSingleStock = async (row) => {
    if (scoringSingle) { message.warning('已有股票正在打分，请稍候'); return; }
    setScoringSingle(row.code);
    try {
      // K 线由后端获取（前端只传股票列表），后端会拉日线并拼入 prompt
      const res = await aiApi.scoreStocks({ stocks: [{ code: row.code, name: row.name, price: row.price }] });
      // 原样展示实际发送给 AI 的内容（含 K 线）
      if (res.data && res.data.sent_prompt) {
        setMessages((m) => [...m, { role: 'user', content: res.data.sent_prompt }]);
      }
      if (res.success && res.data && res.data.list && res.data.list.length) {
        const it = res.data.list[0];
        const scored = {
          code: it.code,
          name: it.name || row.name,
          price: row.price,
          score: it.score,
          analysis: it.analysis || '',
          rank: 0,
        };
        const exist = new Set(finalList.map((s) => s.code));
        const merged = exist.has(scored.code) ? finalList.map((s) => (s.code === scored.code ? { ...scored, rank: s.rank } : s)) : [...finalList, scored];
        merged.sort((a, b) => (b.score || 0) - (a.score || 0));
        merged.forEach((s, i) => { s.rank = i + 1; });
        setFinalList(merged);
        saveFinalList(merged, 'ai');
        message.success(`已打分 ${row.name || row.code}：${it.score} 分`);
        // 展示 AI 实际返回结果
        setMessages((m) => [...m, { role: 'assistant', content: `【${it.code} ${it.name}】${it.score}分：${it.analysis}` }]);
        // 记录 AI 返回结果（持久化，关联当前账户）
        aiApi.saveChat({ role: 'assistant', content: `【${it.code} ${it.name}】${it.score}分：${it.analysis}` }).catch(() => {});
      } else if (res.data && res.data.parse_error) {
        message.error('AI 返回格式解析失败');
      } else {
        message.error(res.message || '打分失败');
      }
    } catch (e) {
      message.error('打分失败：' + (e.response?.data?.message || e.message));
    } finally {
      setScoringSingle('');
    }
  };

  // 终选落库（按日期+代码去重）
  const saveFinalList = async (list, source = 'manual') => {
    try {
      await aiApi.saveFinal({
        date: new Date().toISOString().slice(0, 10),
        source,
        stocks: list.map((s) => ({
          code: s.code, name: s.name, price: s.price,
          score: s.score || 0, analysis: s.analysis || '', rank: s.rank || 0,
        })),
      });
    } catch (e) { /* 静默，落库失败不影响前端展示 */ }
  };

  // 进入页面默认加载今日终选（仅查库，避免刷新丢失）
  const loadTodayFinal = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await aiApi.getFinal({ date: today });
      if (res.success && res.data && res.data.list) {
        const list = res.data.list.map((it, i) => ({
          code: it.code, name: it.name, price: it.price,
          score: it.score, analysis: it.analysis, rank: it.rank || i + 1,
        }));
        setFinalList(list);
      }
    } catch (e) { /* 静默 */ }
  };

  // 初选股票打分：逐只分析（每只一个任务），获取 K 线后单只发给 AI 按规则打分
  const SCORE_BATCH_SIZE = 1;
  const [scoring, setScoring] = useState(false);
  const [scoreTasks, setScoreTasks] = useState([]); // [{batchNo, count, status:'pending'|'running'|'done'|'error', result}]
  const [scoreCursor, setScoreCursor] = useState(-1); // 当前正在执行的批次索引
  const [scoreModalVisible, setScoreModalVisible] = useState(false); // 打分进度弹窗
  const [scoringSingle, setScoringSingle] = useState(''); // 正在单只打分的股票代码

  // 同步更新某个批次任务（同时维护 ref 与 state，保证 finish 时数据一致）
  const updateTask = (idx, patch) => {
    const next = scoreTasksRef.current.map((x, i) => (i === idx ? { ...x, ...patch } : x));
    scoreTasksRef.current = next;
    setScoreTasks(next);
  };

  // 执行单个批次；完成后更新状态并触发下一批（监控式队列）
  const runScoreBatch = async (idx) => {
    setScoreCursor(idx);
    const task = scoreTasksRef.current[idx];
    if (!task) return;
    updateTask(idx, { status: 'running' });
    try {
      // K 线由后端获取（前端只传股票列表）
      const res = await aiApi.scoreStocks({ stocks: task.stocks });
      // 原样展示实际发送给 AI 的内容（含该只 K 线）
      if (res.data && res.data.sent_prompt) {
        setMessages((m) => [...m, { role: 'user', content: res.data.sent_prompt }]);
      }
      if (res.success && res.data && res.data.list && res.data.list.length) {
        const ranked = res.data.list.map((it) => ({
          code: it.code,
          name: it.name || '',
          price: preliminary.find((p) => p.code === it.code)?.price || 0,
          score: it.score,
          analysis: it.analysis || '',
        }));
        updateTask(idx, { status: 'done', result: ranked });
        // 展示 AI 实际返回结果
        const reply = res.data.list.map((it) => `【${it.score}分】${it.code} ${it.name}：${it.analysis}`).join('\n');
        setMessages((m) => [...m, { role: 'assistant', content: reply }]);
        // 记录 AI 返回结果（持久化，关联当前账户）
        aiApi.saveChat({ role: 'assistant', content: reply }).catch(() => {});
      }
      // 解析失败/失败不额外推消息（发送内容已展示，结果缺失即可）
    } catch (e) {
      updateTask(idx, { status: 'error' });
    }
    // 监控：上一批结束 → 触发下一批
    const next = idx + 1;
    if (next < scoreTasksRef.current.length) {
      runScoreBatch(next);
    } else {
      finishScoring();
    }
  };

  // 全部批次完成后的汇总落库
  const finishScoring = () => {
    const all = [];
    let anyError = false;
    scoreTasksRef.current.forEach((t) => {
      if (t.status === 'done' && t.result) t.result.forEach((it) => all.push(it));
      else if (t.status === 'error') { anyError = true; }
    });
    if (all.length > 0) {
      const ranked = all
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .map((it, i) => ({ ...it, rank: i + 1 }));
      const exist = new Set(finalList.map((s) => s.code));
      const merged = [...finalList];
      ranked.forEach((it) => { if (!exist.has(it.code)) merged.push(it); });
      merged.sort((a, b) => (b.score || 0) - (a.score || 0));
      merged.forEach((it, i) => { it.rank = i + 1; });
      setFinalList(merged);
      saveFinalList(merged, 'ai');
      if (anyError) message.warning(`打分部分完成，共 ${ranked.length} 只（有批次失败）`);
      else message.success(`打分完成，共 ${ranked.length} 只，已按分数排序填入终选`);
    } else if (anyError) {
      message.error('全部批次解析/调用失败');
    } else {
      message.error('打分失败：无返回数据');
    }
    setScoring(false);
    setScoreCursor(-1);
  };

  // 启动打分：分页读取数据库收集全部初选 → 拆成「每只一个任务」→ 监控式逐只发给 AI
  const scoreTasksRef = useRef([]);
  const scoreStocks = async () => {
    setScoreModalVisible(true);
    const today = new Date().toISOString().slice(0, 10);
    const READ_PAGE = 200; // 读库分页大小（仅用于拉取，不影响打分粒度）
    // 1) 先查总量
    let total = 0;
    try {
      const first = await aiApi.getPreliminary({ date: today, page: 1, page_size: READ_PAGE });
      if (!first.success) { message.error(first.message || '读取初选失败'); return; }
      total = first.data?.total || (first.data?.list ? first.data.list.length : 0);
    } catch (e) {
      message.error('读取初选失败：' + (e.message || ''));
      return;
    }
    if (total === 0) { message.warning('今日初选股票为空，请先获取初选'); return; }

    // 2) 分页读取数据库，收集全部 stocks（每只拆成一个独立任务，单独获取 K 线并打分）
    const pageCount = Math.ceil(total / READ_PAGE);
    const allStocks = [];
    for (let p = 1; p <= pageCount; p++) {
      try {
        const res = await aiApi.getPreliminary({ date: today, page: p, page_size: READ_PAGE });
        const list = (res.data && res.data.list) || [];
        list.forEach((s) => allStocks.push({ code: s.code, name: s.name, price: s.price }));
      } catch (e) { /* 单页失败忽略，继续后续页 */ }
    }
    const tasks = allStocks.map((s) => ({
      batchNo: 0, // 下面统一重排
      count: 1,
      stocks: [s],
      status: 'pending',
      result: null,
    }));
    // 重排 batchNo
    tasks.forEach((t, i) => { t.batchNo = i + 1; });
    if (tasks.length === 0) { message.warning('今日初选股票为空，请先获取初选'); return; }
    scoreTasksRef.current = tasks;
    setScoreTasks(tasks);
    setScoring(true);
    setScoreCursor(-1);
    // 启动第一只；后续由 runScoreBatch 监控触发（逐只的发送内容与结果由 runScoreBatch 推入聊天框）
    runScoreBatch(0);
  };

  // ===== 策略相关 =====
  const loadStrategies = async () => {
    setStratLoading(true);
    try {
      const res = await strategyApi.list();
      if (res.success) setStrategies(res.data || []);
      else message.error(res.message || '加载策略失败');
    } catch (e) {
      message.error('加载策略失败：' + (e.message || ''));
    } finally {
      setStratLoading(false);
    }
  };

  const openStrategyManage = () => {
    setStrategyOpen(true);
    loadStrategies();
    setStratFormOpen(false);
    setEditing(null);
  };

  // ===== 策略表单（新增/编辑） =====
  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setStratFormOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      algo: row.algo,
      description: row.description,
      params: row.params,
      enabled: row.enabled !== 0,
    });
    setStratFormOpen(true);
  };
  const submitStrat = async () => {
    const vals = await form.validateFields();
    const payload = {
      name: vals.name,
      algo: vals.algo || '',
      description: vals.description || '',
      params: vals.params || '',
      enabled: vals.enabled ? 1 : 0,
    };
    try {
      let res;
      if (editing) res = await strategyApi.update(editing.id, payload);
      else res = await strategyApi.create(payload);
      if (res.success) {
        message.success(editing ? '已更新策略' : '已新增策略');
        setStratFormOpen(false);
        loadStrategies();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (e) {
      message.error('保存失败：' + (e.message || ''));
    }
  };
  const deleteStrat = async (id) => {
    try {
      const res = await strategyApi.remove(id);
      if (res.success) {
        message.success('已删除策略');
        loadStrategies();
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (e) {
      message.error('删除失败：' + (e.message || ''));
    }
  };

  const preColumns = [
    { title: '股票代码', dataIndex: 'code', key: 'code', width: 90 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 90 },
    {
      title: '价格', dataIndex: 'price', key: 'price', width: 80, align: 'right',
      render: (v) => <span style={{ color: '#cf1322' }}>{v ? v.toFixed(2) : '-'}</span>,
    },
    { title: '策略标识', dataIndex: 'strategy', key: 'strategy', width: 100,
      render: (v) => (v ? <Tag color="blue">{v}</Tag> : '-') },
    { title: '获取时间', dataIndex: 'fetch_time', key: 'fetch_time', width: 130 },
    {
      title: '', key: 'act', width: 120,
      render: (_, r) => (
        <Space size={2}>
          <Button type="link" size="small" icon={<ThunderboltOutlined />} loading={scoringSingle === r.code} onClick={() => scoreSingleStock(r)}>发送AI打分</Button>
        </Space>
      ),
    },
  ];

  const finalColumns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 50, render: (v) => <Tag color="gold">{v}</Tag> },
    { title: '股票代码', dataIndex: 'code', key: 'code', width: 90 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 90 },
    {
      title: '价格', dataIndex: 'price', key: 'price', width: 80, align: 'right',
      render: (v) => <span style={{ color: '#cf1322' }}>{v ? v.toFixed(2) : '-'}</span>,
    },
    {
      title: '得分', dataIndex: 'score', key: 'score', width: 70, align: 'right',
      sorter: (a, b) => (b.score || 0) - (a.score || 0),
      render: (v) => v != null ? <Tag color={v >= 80 ? 'red' : v >= 60 ? 'orange' : 'default'}>{v}</Tag> : '-',
    },
    {
      title: '分析', dataIndex: 'analysis', key: 'analysis', width: 160, ellipsis: true,
      render: (v) => <span style={{ color: '#888' }}>{v || '-'}</span>,
    },
    {
      title: '', key: 'act', width: 110,
      render: (_, r) => (
        <Space size={2}>
          <Button type="link" size="small" danger onClick={() => removeFinal(r.code)}>移除</Button>
        </Space>
      ),
    },
  ];

  const stratColumns = [
    { title: '策略名称', dataIndex: 'name', key: 'name' },
    {
      title: '算法摘要', dataIndex: 'algo', key: 'algo', ellipsis: true,
      render: (v) => <span style={{ color: '#666' }}>{v ? (v.length > 40 ? v.slice(0, 40) + '…' : v) : '-'}</span>,
    },
    {
      title: '状态', dataIndex: 'enabled', key: 'enabled', width: 70,
      render: (v) => (v !== 0 ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>),
    },
    {
      title: '操作', key: 'act', width: 130,
      render: (_, r) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(r)}>编辑</Button>
          <Popconfirm title="确认删除该策略?" onConfirm={() => deleteStrat(r.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 120px)', minHeight: 480 }}>
      {/* 左侧 AI 聊天框 */}
      <Card
        title={<span style={{ color: '#eee' }}><RobotOutlined /> 股小T <Tag color="blue" style={{ marginLeft: 4 }}>通义千问</Tag></span>}
        style={{ width: '38%', minWidth: 320, display: 'flex', flexDirection: 'column', background: '#1f1f1f', borderColor: '#303030' }}
        styles={{ header: { background: '#1f1f1f', borderBottom: '1px solid #303030' } }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden', background: '#1f1f1f' }}
      >
        <div style={{ padding: '10px 12px 0', display: 'flex', gap: 8, background: '#1f1f1f' }}>
          <Button icon={<SettingOutlined />} onClick={openStrategyManage}>策略维护</Button>
          <Button type="primary" ghost icon={<StarOutlined />} loading={scoring} onClick={scoreStocks} disabled={scoring}>
            {scoring ? `打分中(${scoreCursor + 1}/${scoreTasks.length}批)` : '初选股票打分'}
          </Button>
          <Popconfirm title="确认清空聊天记录？" description="仅清空当前账户，软删除可恢复" onConfirm={clearChat} okText="清空" cancelText="取消">
            <Button icon={<DeleteOutlined />} danger style={{ marginLeft: 'auto' }}>清空聊天</Button>
          </Popconfirm>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#121212' }}>
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  maxWidth: '80%', padding: '8px 12px', borderRadius: 8,
                  background: m.role === 'user' ? '#1890ff' : '#2a2a2a',
                  color: m.role === 'user' ? '#fff' : '#eee',
                  border: m.role === 'user' ? 'none' : '1px solid #3a3a3a',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}
              >
                {m.image && (
                  <Image
                    src={m.image}
                    style={{ maxWidth: 160, borderRadius: 4, marginBottom: m.content ? 6 : 0, display: 'block' }}
                  />
                )}
                {m.content}
              </div>
            </div>
          ))}
          {chatLoading && <Text type="secondary">AI 思考中…</Text>}
          <div ref={chatEndRef} />
        </div>
        <div style={{ padding: 12, borderTop: '1px solid #303030', background: '#1f1f1f' }}>
          {pendingImage && (
            <div style={{ marginBottom: 8, position: 'relative', display: 'inline-block' }}>
              <Image src={pendingImage.url} style={{ maxWidth: 90, borderRadius: 4 }} />
              <Button
                size="small" danger shape="circle" icon={<DeleteOutlined />}
                style={{ position: 'absolute', top: -8, right: -8 }}
                onClick={() => setPendingImage(null)}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <Upload
              beforeUpload={beforeImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<PictureOutlined />} disabled={chatLoading}>图片</Button>
            </Upload>
            <Input.TextArea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入选股需求，回车发送…（可附图片）"
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={chatLoading} onClick={sendMessage}>
              发送
            </Button>
          </div>
        </div>
      </Card>

      {/* 右侧 选股列表（窄布局） */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        <Card
          size="small"
          title="初选股票"
          extra={
            <Space size={8}>
              <DatePicker
                value={dateFilter}
                allowClear={false}
                format="YYYY-MM-DD"
                onChange={(d) => { if (d) { setDateFilter(d); loadPreliminaryByDate(d); } }}
                style={{ width: 140 }}
              />
              <Input.Search
                allowClear
                size="small"
                placeholder="搜索代码/名称"
                value={preliminarySearch}
                onChange={(e) => setPreliminarySearch(e.target.value)}
                style={{ width: 160 }}
              />
              <Button type="primary" size="small" icon={<ReloadOutlined />} loading={loadingPre} onClick={fetchPreliminary}>
                获取初选股票
              </Button>
            </Space>
          }
          style={{ flex: 1 }}
          bodyStyle={{ padding: 0, overflow: 'auto' }}
        >
          {loadingPre ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
          ) : (
            <Table
              rowKey={(r) => r.code || Math.random()}
              columns={preColumns}
              dataSource={filteredPre}
              pagination={{ pageSize: 50, showSizeChanger: false, showQuickJumper: true, showTotal: (t) => `共 ${t} 条` }}
              size="small"
              locale={{ emptyText: '点击"获取初选股票"拉取列表' }}
              scroll={{ y: 'calc(100vh - 360px)' }}
            />
          )}
        </Card>

        <Card
          size="small"
          title="终选股票排行"
          extra={<Tag color="blue">{finalList.length} 只</Tag>}
          style={{ flex: 1 }}
          bodyStyle={{ padding: 0, overflow: 'auto' }}
        >
          <Table
            rowKey={(r) => r.code || Math.random()}
            columns={finalColumns}
            dataSource={finalList}
            pagination={false}
            size="small"
            locale={{ emptyText: '从初选列表中点击"终选"加入排行' }}
            scroll={{ y: 'calc(100vh - 320px)' }}
          />
        </Card>
      </div>

      {/* 初选打分进度弹窗 */}
      <Modal
        title="初选股票打分"
        open={scoreModalVisible}
        onCancel={() => { if (!scoring) setScoreModalVisible(false); }}
        footer={[
          <Button key="close" disabled={scoring} onClick={() => setScoreModalVisible(false)}>
            {scoring ? '打分进行中…' : '关闭'}
          </Button>,
        ]}
        width={620}
        maskClosable={false}
      >
        <div style={{ marginBottom: 12 }}>
          <Text>从数据库分页读取今日初选，按每批 {SCORE_BATCH_SIZE} 只逐批发给 AI 打分（每只附带日线 K 线，按 日线走势30 / 可买性35 / 近3日上涨35 三维加权，满分100），完成后自动写入终选。</Text>
          <div style={{ marginTop: 8 }}>
            <Tag color={scoring ? 'processing' : scoreTasks.length ? 'success' : 'default'}>
              {scoring ? `进行中 (${scoreCursor + 1}/${scoreTasks.length})` : (scoreTasks.length ? '已完成' : '未开始')}
            </Tag>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
          {scoreTasks.map((t, i) => {
            const color = t.status === 'done' ? 'green' : t.status === 'error' ? 'red' : t.status === 'running' ? 'blue' : 'default';
            const label = t.status === 'done' ? '完成' : t.status === 'error' ? '失败' : t.status === 'running' ? '执行中' : '等待';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <Tag color={color} style={{ marginRight: 0 }}>{label}</Tag>
                <span>第 {t.batchNo}/{scoreTasks.length} 批</span>
                <span style={{ color: '#999' }}>（{t.count} 只）</span>
                {t.status === 'done' && t.result && <span style={{ color: '#999' }}>返回 {t.result.length} 只</span>}
              </div>
            );
          })}
        </div>
      </Modal>

      {/* 策略维护弹窗 */}
      <Modal
        title="策略维护"
        open={strategyOpen}
        onCancel={() => setStrategyOpen(false)}
        width={820}
        footer={[
          <Button key="add" type="primary" onClick={openCreate}>新增策略</Button>,
          <Button key="close" onClick={() => setStrategyOpen(false)}>关闭</Button>,
        ]}
      >
        <Table
          rowKey="id"
          columns={stratColumns}
          dataSource={strategies}
          loading={stratLoading}
          pagination={false}
          size="small"
          locale={{ emptyText: '暂无策略，点击「新增策略」创建' }}
        />

        <Modal
          title={editing ? '编辑策略' : '新增策略'}
          open={stratFormOpen}
          onCancel={() => setStratFormOpen(false)}
          onOk={submitStrat}
          okText="保存"
          destroyOnClose
        >
          <Form form={form} layout="vertical">
            <Form.Item name="name" label="策略名称" rules={[{ required: true, message: '请输入策略名称' }]}>
              <Input placeholder="例如：多头排列突破" />
            </Form.Item>
            <Form.Item name="algo" label="策略算法（通达信公式）">
              <Input.TextArea rows={6} placeholder="例如：&#10;CROSS(MA(C,5),MA(C,20)) AND VOL>REF(VOL,1)*1.5;" />
            </Form.Item>
            <Form.Item name="description" label="策略说明">
              <Input.TextArea rows={2} placeholder="策略逻辑简要说明" />
            </Form.Item>
            <Form.Item name="params" label="策略参数（JSON，可选）">
              <Input placeholder='例如：{"n":20,"threshold":1.5}' />
            </Form.Item>
            <Form.Item name="enabled" label="是否启用" initialValue={1}>
              <Select>
                <Select.Option value={1}>启用</Select.Option>
                <Select.Option value={0}>停用</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </Modal>
    </div>
  );
}
